import { MouseEvent } from 'react';

import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import {
  GridColumnMenuItemProps,
  GridPinnedColumnPosition,
  useGridRootProps,
} from '@mui/x-data-grid-premium';

import { ContextMenuUnpinIcon } from './GeneratikTable.icons';
import { useGeneratikTableContext } from './GeneratikTableContext';
import { unpinColumn } from './contextMenu/tableContextMenuActions';

/**
 * Same behaviour as MUI's default columnMenuPinningItem, but the "Unpin" row
 * gets an icon — the default component hardcodes an empty ListItemIcon for
 * it, with no slot to override.
 */
const GeneratikTableColumnMenuPinningItem = ({ colDef, onClick }: GridColumnMenuItemProps) => {
  const ctx = useGeneratikTableContext();
  const rootProps = useGridRootProps();
  const apiRef = ctx.apiRef;

  const pinColumn = (side: GridPinnedColumnPosition) => (event: MouseEvent<HTMLLIElement>) => {
    apiRef.current.pinColumn(colDef.field, side);
    onClick(event);
  };

  const handleUnpin = (event: MouseEvent<HTMLLIElement>) => {
    unpinColumn(apiRef, colDef.field);
    onClick(event);
  };

  const side = apiRef.current.isColumnPinned(colDef.field);

  if (side) {
    const otherSide =
      side === GridPinnedColumnPosition.RIGHT
        ? GridPinnedColumnPosition.LEFT
        : GridPinnedColumnPosition.RIGHT;
    const label = otherSide === GridPinnedColumnPosition.RIGHT ? 'pinToRight' : 'pinToLeft';
    const OtherSideIcon =
      side === GridPinnedColumnPosition.RIGHT
        ? rootProps.slots.columnMenuPinLeftIcon
        : rootProps.slots.columnMenuPinRightIcon;

    return (
      <>
        <MenuItem onClick={pinColumn(otherSide)}>
          <ListItemIcon>
            <OtherSideIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText(label)}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleUnpin}>
          <ListItemIcon>
            <ContextMenuUnpinIcon />
          </ListItemIcon>
          <ListItemText>{apiRef.current.getLocaleText('unpin')}</ListItemText>
        </MenuItem>
      </>
    );
  }

  return (
    <>
      <MenuItem onClick={pinColumn(GridPinnedColumnPosition.LEFT)}>
        <ListItemIcon>
          <rootProps.slots.columnMenuPinLeftIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{apiRef.current.getLocaleText('pinToLeft')}</ListItemText>
      </MenuItem>
      <MenuItem onClick={pinColumn(GridPinnedColumnPosition.RIGHT)}>
        <ListItemIcon>
          <rootProps.slots.columnMenuPinRightIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{apiRef.current.getLocaleText('pinToRight')}</ListItemText>
      </MenuItem>
    </>
  );
};

export default GeneratikTableColumnMenuPinningItem;
